/* The library's data model. Everything here is plain JSON (ADR 0009, 0018). */
import type { Severity } from '../core/types';

export const SCHEMA = 1;             // bump + add a migration when a file format changes
export const ANALYSIS_VERSION = 3;
/** Bump when the verdict rules change: stored verdicts are re-checked from stored analyses, no decoding (2: gentle roll-offs, 3: quiet content above the fade, ADR 0033). */
export const VERDICT_VERSION = 8;   // bump to re-analyse every track after an algorithm change (2: full analysis stored, 3: fingerprints, 4: content beyond a wall, 2026-09-27, 5: holes under a wall, 2026-09-28, 6: a quiet top end is still hi-res, upsamples found by what reaches past 24 kHz, 7: a mastering lowpass from 17 kHz, 8: content beyond a wall without frequent drop-outs is lossless, 2026-09-30)

export interface ProfileRef { id: string; name: string; color: string }
/** A profile as the user sees it (ADR 0113): an artist alias, the account's (the same list on every device). */
export interface Alias { id: string; name: string; color: string; bpmRange?: 'half' | 'full' }
/** computer: the account device this GLUE folder's computer is (ADR 0108), remembered for when neither GLUE Home
    nor a sign-in says.
    ADR 0113: `aliases` (the account's, kept here for when it's offline), `lastAlias` (who used GLUE last), and
    `container`: the one profile folder that holds this GLUE folder's library (every alias uses it). `profiles`
    and `lastProfile` are the storage folders, as before (older GLUE versions read them). */
export interface HomeIndex { schemaVersion: number; profiles: ProfileRef[]; lastProfile: string | null; appearance?: { theme: string; mode: 'dark' | 'light' | 'system'; at?: number }; guide?: import('../core/guide/state').GuideState; computer?: string; aliases?: Alias[]; aliasesV?: number; lastAlias?: string | null; container?: string | null }

export interface CollectionRef { id: string; name: string }
/** cloudSync: this device keeps a copy of the profile's data in GLUE Cloud (ADR 0040). Absent = on
    while signed in (ADR 0042); false = the user turned it off. */
/** bpmRange: how BPMs are shown (ADR 0052): as detected (absent), folded into 60–120 ('half') or 120–240 ('full'). */
export interface Profile { schemaVersion: number; id: string; name: string; color: string; createdAt: string; collections: CollectionRef[]; lastCollection: string | null; cloudSync?: boolean; bpmRange?: 'half' | 'full' }

/** What the user set on a track's Prepare tab (ADR 0052). Overrides the analysis; "Re-analyse" clears
    bpm, beat0 and bar (the grid), never the cues. */
export interface Prep {
  bpm?: number;       // the tempo, corrected
  beat0?: number;     // the grid: the first beat's time (s)
  bar?: number;       // which of each four beats is the bar's first (0–3)
  flip?: boolean;     // shown at the other octave (a 140 track as 70)
  cues?: import('../core/interop/types').CuePoint[];
}

/** A music folder the user granted (handle lives in IndexedDB under `handleKey`). `hidden`: a folder
    GLUE keeps for itself, not listed under Music folders: GLUE Home's incoming folder (ADR 0051). */
export interface Root { id: string; name: string; absPath: string | null; handleKey: string; addedAt: string; hidden?: boolean }
/** The id of GLUE Home's incoming folder as a music folder of a collection (the same on every computer:
    each computer's collection has its own). Its songs are TO BE SORTED. */
export const INCOMING_ROOT = 'incoming';
/** tags: tags made in GLUE, kept even while no track uses them (ADR 0032). cloudMerged: it has been
    merged with other devices once (ADR 0042), so an unmerge is kept rather than merged again.
    dupApart: pairs of songs the user said aren't duplicates ("Keep · not a duplicate", pairKey); dupManual: groups the
    user marked as duplicates by hand (ADR 0117). mainRoot: the user's main music folder, whose copy is the best among
    equals (ADR 0121). djDismissed: found DJ libraries the user took off the list (place|relPath of each way to the file). */
export interface Collection { schemaVersion: number; id: string; name: string; createdAt: string; roots: Root[]; ignoredDupes?: string[]; dupBest?: Record<string, string>; dupConfirmed?: string[]; dupApart?: string[]; dupManual?: string[][]; mainRoot?: string; djDismissed?: string[]; relinkNo?: string[]; genres?: string[]; autoAnalyse?: boolean; tags?: string[]; cloudMerged?: boolean;
  /** Moved into this shared collection (ADR 0096): kept as it was, read only, no longer synced. */
  movedTo?: string;
  /** The songs that got another id there (ADR 0102). */
  movedIds?: Record<string, string> }

export type TrackStatus = 'linked' | 'unlinked' | 'missing';
export interface TrackFormat { container: string; codec: string; lossless: boolean | null; sampleRate: number; bits: number; bitrate: number; channels: number }
export interface Track {
  id: string;
  status: TrackStatus;
  rootId: string | null;        // where the file is (linked tracks)
  relPath: string | null;       // path inside the root, '/'-separated
  /** A song added on its own: 'file:…' = its handle's key in IndexedDB, 'copy:<path>' = a copy in the
      GLUE folder (browsers without file handles). Absent for folder and imported tracks. */
  fileKey?: string | null;
  importPath: string | null;    // absolute path as an imported library saw it
  /** Where a song added on its own is on this computer (GLUE Home found it, ADR 0125: the browser never says). */
  filePath?: string;
  /** Other DJ-library records that are this song: their paths, linked by the user from "No file linked" (ADR 0124).
      A new read of the library takes them for this song (their own files are gone). */
  aka?: string[];
  fileName: string;
  size: number | null;
  mtime: number | null;
  title: string; artist: string; album: string; genre: string; label: string; comment: string; year: string;
  duration: number | null;
  format: TrackFormat | null;
  addedAt: string;
  rating?: number | null;       // the user's own rating in GLUE: 0.5–5 in half stars
  notes?: string;               // the user's own notes about the track
  grouping?: string;            // the file's / DJ app's Grouping field
  tags?: string[];              // the user's tags; absent until edited, then the found ones (tagsOf) stand in
  /** Song info edited in GLUE (ADR 0071): never filled in again from the file or a DJ app. */
  edited?: string[];
  /** Of those, the ones not in the file yet: GLUE Home writes them when it runs. */
  unwritten?: string[];
  /** The verdict label the user marked as fine (a false positive): shown as fine while it's GLUE's verdict. */
  markedFine?: string;
  /** The Discogs record (release id) the user chose this song for, and the ones they took it off (ADR 0188). */
  discogs?: number;
  discogsNo?: number[];
  art?: string;                 // the cover's hash (ADR 0072): '' none; absent: not looked for yet
  prep?: Prep;                  // the Prepare tab: corrected tempo and grid, display flip, cues (ADR 0052)
  onDevices?: string[];         // cloud views and merged collections: the devices that have this track (never saved)
  /** A track from another device of a merged collection, shown here from the cloud (ADR 0042; never saved):
      that device, and its own profile, collection and track ids (to stream it through GLUE Home, ADR 0045). */
  remote?: { device: string; name: string; profile?: string; collection?: string; id?: string;
    /** Where the file is on that computer: its music folder and path (a shared collection, ADR 0100). */
    where?: string;
    /** A song waiting in a GLUE Home's incoming folder (TO BE SORTED, ADR 0046): that GLUE Home and the file's name. */
    home?: string; incoming?: string;
    /** Another computer's song whose file is also in a GLUE Home's incoming folder: played from there. */
    via?: { home: string; incoming: string } };
  sources: string[];            // ids of imported sources that contain this track
}

export interface AnalysisSummary {
  v: number; at: string;
  grade: 'ok' | 'warn' | 'bad' | 'info'; label: string; headline: string;
  fc: number; wall: boolean; full: boolean; effBits: number | null; declaredBits: number;
  origin: string;
  bpm: number | null;
  key: { tonic: number; mode: 'major' | 'minor'; margin: number; tuning: number } | null;
  findings: { sev: Severity; title: string }[];
  fileSize: number; fileMtime: number;   // to notice when the file changes
  error?: string;
  fp?: boolean;                           // an acoustic fingerprint is stored for this analysis
  vv?: number;                            // the VERDICT_VERSION the verdict came from (absent = 1)
  engine?: string;                        // what made it: "glue-audio <version>" for GLUE Home's native engine (ADR 0147), absent for JavaScript
}

export interface List {
  schemaVersion: number;
  id: string; kind: 'folder' | 'playlist'; name: string; parentId: string | null; position: number;
  notes: string; items: string[];                          // track ids, in order
  /** A DJ library's playlist or folder brought into GLUE, and kept in step with it (ADR 0063). externalId ''
      is the library's own folder. chain: a folder made only to hold what was imported below it, which
      doesn't take the rest of the library's folder. */
  origin: { sourceId: string; externalId: string; chain?: boolean } | null;
  color?: string | null;                                   // one of LIST_COLORS, or none
  auto?: Record<string, unknown>;                          // how an automatic playlist was made (options, seed, date)
  tags?: string[];                                         // the playlist's own tags
  /** An event's folder (its id), or the Events folder that holds them ('*') (ADR 0074). */
  event?: string;
  /** The DJ apps this list (and, for a folder, everything in it) is kept in, under their "GLUE" folder (ADR 0180):
      'engine'. Only GLUE's own lists (no origin). */
  apps?: string[];
  /** A Discogs playlist (ADR 0189), shown under Discogs only: its items mix song ids and vinyl tracks
      (`dg:<release>:<position>`); an auto one keeps `rules` and no items. */
  discogs?: { rules?: import('../core/library/discogsLists').DiscogsRules };
  createdAt: string;
}

/** What an imported library said about a track (kept read-only, per source). */
export interface SourceTrack { externalId: string; trackId: string; bpm: number | null; key: string | null; rating: number | null; playCount: number | null; cues: number; dateAdded: string | null; path: string; cueList?: import('../core/interop/types').CuePoint[]; grid?: import('../core/interop/types').Grid }
export type SourceApp = 'rekordbox' | 'engine' | 'serato' | 'traktor' | 'apple' | 'm3u';
/** Where a detected library was imported from (place: a music folder id, 'home', or a remembered place), for Update. */
export interface SourceOrigin { place: string; relPath: string; modified: number }
/** A DJ library's playlist or folder as the library has it (ADR 0063). items: its tracks' externalIds. */
export interface SourceList { externalId: string; kind: 'folder' | 'playlist'; name: string; parent: string | null; items: string[];
  /** Edited in GLUE and not written into the app yet (ADR 0179). An item `glue:<song>` is a song it doesn't have yet. */
  wait?: boolean }
/** An edit of the main Engine DJ library's own playlists, in GLUE's DJ collection (ADR 0179; GLUE Home's `djEdit`):
    lists by their `externalId` (a new one's id is GLUE Home's until it's written); `parent` '' is the top; `before` the
    list or entry it goes before (none: last); `songs` GLUE's songs, `items` the list's entries. */
export type DjEdit =
  | { t: 'new'; name: string; parent: string; kind: 'playlist' | 'folder'; before?: string }
  | { t: 'rename'; list: string; name: string }
  | { t: 'move'; list: string; parent: string; before?: string }
  | { t: 'delete'; list: string }
  | { t: 'add'; list: string; songs: string[]; before?: string }
  | { t: 'remove'; list: string; items: string[] }
  | { t: 'shift'; list: string; items: string[]; before?: string }
  /** The drives' copies of a list (and what's in it; '' all) made as the Collection has them (2026-10-13). */
  | { t: 'align'; list: string };
/** tree: the library's playlists as last read, browsed in the sidebar; they come into GLUE on demand. */
export interface Source { schemaVersion: number; id: string; app: SourceApp; name: string; fileName: string; importedAt: string; tracks: SourceTrack[]; lists: number; origin?: SourceOrigin; tree?: SourceList[];
  /** Lists the library's last read didn't have, with when that was first seen: GLUE's copies go only when
      a read at least a minute later still lacks them (ADR 0090). */
  pendingGone?: Record<string, number>;
  /** The main DJ library (ADR 0169): of this computer's, the one whose grids and cues a song shows by default, after
      GLUE's own, and the one GLUE syncs with (ADR 0168). One per computer, kept through every read. */
  main?: boolean;
  /** GLUE's changes written into it too (ADR 0170): the main DJ library kept in step both ways by GLUE Home. Opt-in. */
  sync?: boolean;
  /** In a shared collection, the computer whose library this is (ADR 0099): only it reads, follows and removes it. */
  computer?: string }

export const newId = () => crypto.randomUUID().replace(/-/g, '').slice(0, 16);
export const shardOf = (id: string) => id.slice(0, 2);
export const LIST_COLORS = ['#ff6b6b', '#ff9f5a', '#f2c14e', '#3ecf8e', '#4fd1c5', '#7cc7ff', '#a78bfa', '#f472b6'];
export const PROFILE_COLORS = ['#7cc7ff', '#f472b6', '#4fd1c5', '#ff9f5a', '#a78bfa', '#f2c14e', '#3ecf8e', '#ff6b6b'];
